import { useState, useEffect, useRef } from 'react';
import { supabase, safeRefreshSession } from './supabase';
import { supabaseData } from './supabaseData';
import {
  Zap, Clock, CheckCircle, Play, Eye, Calendar,
  MapPin, Phone, DollarSign, MessageSquare, Navigation, ChevronDown, ChevronUp, Send, X, Edit2, Trash2, Lock, Sparkles,
  UserPlus, Mail, Link, Loader, FileText, Package
} from 'lucide-react';
import JobCompletionModal from './JobCompletionModal';
import QuoteVisitCompletionModal from './QuoteVisitCompletionModal';
import JobPhotos from './JobPhotos';
import { useModal } from './AppModal';
import { useTheme } from './ThemeContext';
import { useFeatures } from './FeatureContext';
import { getTodayString } from './dateUtils';
import { sendSMS, replaceTemplateVariables } from './smsUtils';
import { isOfflineModeEnabled, getCachedData } from './offlineCache';
import { captureLocation } from './locationTracker';
import InventoryHealthWidget from './InventoryHealthWidget';

export default function Today({ companyId, companyData, onNavigate, user, isMobile }) {
  const { colors } = useTheme();
  const { showSuccess, showError } = useModal();
  const { hasFeature } = useFeatures();
  const textingEnabled = hasFeature('texting');

  const [activeTab, setActiveTab] = useState('today');
  const [loading, setLoading] = useState(true);
  const [upcomingJobs, setUpcomingJobs] = useState([]);
  const [inProgressJobs, setInProgressJobs] = useState([]);
  const [completedJobs, setCompletedJobs] = useState([]);
  const [expandedJobId, setExpandedJobId] = useState(null);
  const [messageTexts, setMessageTexts] = useState({}); // Per-job message text

  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [selectedJobForCompletion, setSelectedJobForCompletion] = useState(null);
  const [showQuoteVisitModal, setShowQuoteVisitModal] = useState(false);
  const [selectedQuoteVisitJob, setSelectedQuoteVisitJob] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [editingJob, setEditingJob] = useState(null);
  const [saving, setSaving] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [sendingQuickMsg, setSendingQuickMsg] = useState({}); // Track sending state per job
  const [paymentBanner, setPaymentBanner] = useState(null); // 'card_reminder', 'financing_reminder', or null
  const [stripeConnected, setStripeConnected] = useState(false);

  // Low stock alerts
  const [lowStockItems, setLowStockItems] = useState([]);

  // Customer invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [invitePhone, setInvitePhone] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMethod, setInviteMethod] = useState('email');
  const [sendingInvite, setSendingInvite] = useState(false);
  const [inviteSent, setInviteSent] = useState(false);
  const [inviteUrl, setInviteUrl] = useState('');

  // Check what payment reminder banner to show (if any)
  useEffect(() => {
    if (!companyId) return;
    checkPaymentBanners();
  }, [companyId]);

  const checkPaymentBanners = async () => {
    try {
      // Get company Stripe status
      const { data: company } = await supabaseData
        .from('companies')
        .select('stripe_connect_id, stripe_charges_enabled')
        .eq('id', companyId)
        .single();

      const isConnected = !!company?.stripe_connect_id && company?.stripe_charges_enabled;
      setStripeConnected(isConnected);

      // If not connected to Stripe, don't show any payment banners
      if (!isConnected) {
        setPaymentBanner(null);
        return;
      }

      // Get payment stats - last Stripe payment and whether they've done BNPL
      let payments = [];
      try {
        const { data, error } = await supabaseData
          .from('invoices')
          .select('created_at, payment_method_details, status')
          .eq('company_id', companyId)
          .eq('status', 'paid')
          .not('payment_method_details', 'is', null)
          .order('created_at', { ascending: false });
        if (!error) payments = data || [];
      } catch (e) {
        // Table doesn't exist yet - that's fine
      }

      const now = new Date();
      const oneWeekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
      const twoWeeksAgo = new Date(now - 14 * 24 * 60 * 60 * 1000);

      // Check for any Stripe payments (those with payment_method_details containing stripe data)
      const stripePayments = (payments || []).filter(p => {
        const details = p.payment_method_details;
        if (!details) return false;
        // Check if it's a Stripe payment (has card, afterpay, klarna, affirm, etc.)
        if (Array.isArray(details)) {
          return details.some(d => d.paid_via === 'stripe' || d.type === 'card' || ['afterpay', 'klarna', 'affirm'].includes(d.brand));
        }
        return details.paid_via === 'stripe' || details.type === 'card' || ['afterpay', 'klarna', 'affirm'].includes(details.brand);
      });

      // Check if they've done any BNPL payment
      const hasDoneBnpl = stripePayments.some(p => {
        const details = p.payment_method_details;
        if (Array.isArray(details)) {
          return details.some(d => ['afterpay', 'klarna', 'affirm'].includes(d.brand) ||
            ['afterpay_clearpay', 'klarna', 'affirm'].includes(d.type));
        }
        return ['afterpay', 'klarna', 'affirm'].includes(details?.brand) ||
          ['afterpay_clearpay', 'klarna', 'affirm'].includes(details?.type);
      });

      // Last Stripe payment date
      const lastStripePayment = stripePayments.length > 0 ? new Date(stripePayments[0].created_at) : null;

      // Check dismissed timestamps from localStorage
      const cardReminderDismissed = localStorage.getItem('cardReminderDismissed');
      const financingReminderDismissed = localStorage.getItem('financingReminderDismissed');

      // Priority 1: Card payment reminder if no Stripe payments in 1+ week
      if (!lastStripePayment || lastStripePayment < oneWeekAgo) {
        // Show card reminder if not dismissed in the last week
        if (!cardReminderDismissed || new Date(cardReminderDismissed) < oneWeekAgo) {
          setPaymentBanner('card_reminder');
          return;
        }
      }

      // Priority 2: Financing reminder if never done BNPL (every 2 weeks)
      if (!hasDoneBnpl) {
        // Show financing reminder if not dismissed in the last 2 weeks
        if (!financingReminderDismissed || new Date(financingReminderDismissed) < twoWeeksAgo) {
          setPaymentBanner('financing_reminder');
          return;
        }
      }

      // No banner needed
      setPaymentBanner(null);
    } catch (error) {

      setPaymentBanner(null);
    }
  };

  const dismissPaymentBanner = (type) => {
    const now = new Date().toISOString();
    if (type === 'card_reminder') {
      localStorage.setItem('cardReminderDismissed', now);
    } else if (type === 'financing_reminder') {
      localStorage.setItem('financingReminderDismissed', now);
    }
    setPaymentBanner(null);
  };

  // Quick-send message templates
  const quickTemplates = [
    { id: 'omw', label: 'On My Way', message: 'Hi {customer_name}, this is your tech from {company_name}. I\'m on my way and should arrive in about 15 minutes!' },
    { id: 'late', label: 'Running Late', message: 'Hi {customer_name}, I\'m running about 30 minutes behind schedule. I apologize for the delay and will be there as soon as possible.' },
    { id: 'arrived', label: 'Arrived', message: 'Hi {customer_name}, I\'ve arrived at your location. Please let me know if you have any questions!' }
  ];


  useEffect(() => {
    if (companyId) {
      loadTodaysJobs();
      loadCompanyLogo();
      loadLowStockAlerts();
    } else {
      // No companyId yet, stop loading
      setLoading(false);
    }
  }, [companyId]);

  const loadLowStockAlerts = async () => {
    try {
      const { data: company } = await supabaseData
        .from('companies')
        .select('inventory_enabled, inventory_low_stock_alerts')
        .eq('id', companyId)
        .single();

      if (!company?.inventory_enabled || !company?.inventory_low_stock_alerts) return;

      const { data: defaultLoc } = await supabaseData
        .from('locations')
        .select('id')
        .eq('company_id', companyId)
        .eq('is_default', true)
        .single();

      if (!defaultLoc) return;

      const { data: items } = await supabaseData
        .from('inventory_items')
        .select('id, name, reorder_point, reorder_quantity')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .gt('reorder_point', 0);

      if (!items || items.length === 0) return;

      const { data: stock } = await supabaseData
        .from('inventory_stock')
        .select('inventory_item_id, quantity_on_hand')
        .eq('location_id', defaultLoc.id)
        .in('inventory_item_id', items.map(i => i.id));

      const stockMap = {};
      (stock || []).forEach(s => { stockMap[s.inventory_item_id] = s.quantity_on_hand || 0; });

      const lowItems = items.filter(item => {
        const qty = stockMap[item.id] || 0;
        return qty <= item.reorder_point;
      }).map(item => ({
        ...item,
        quantity_on_hand: stockMap[item.id] || 0
      }));

      setLowStockItems(lowItems);
    } catch (error) {
      // Non-critical
    }
  };

  const loadCompanyLogo = async () => {
    try {
      const { data } = await supabaseData
        .from('companies')
        .select('logo_url, name, quote_company_name')
        .eq('id', companyId)
        .single();
      setCompanyLogo(data?.logo_url || null);
      setCompanyName(data?.quote_company_name || data?.name || 'Our Company');
    } catch (error) {

    }
  };

  const loadTodaysJobs = async (retryCount = 0) => {
    try {
      // Offline: filter cached jobs by today's date
      if (!navigator.onLine && isOfflineModeEnabled()) {
        const cachedJobs = getCachedData('jobs', companyId);
        if (cachedJobs) {
          const today = getTodayString();
          const todaysJobs = cachedJobs.filter(j => j.scheduled_date === today);
          setUpcomingJobs(todaysJobs.filter(j => j.status === 'scheduled'));
          setInProgressJobs(todaysJobs.filter(j => j.status === 'in-progress'));
          setCompletedJobs(todaysJobs.filter(j => j.status === 'completed'));
          setLoading(false);
          return;
        }
      }

      setLoading(true);
      const today = getTodayString(); // Use local timezone, not UTC

      const { data: jobs, error } = await supabaseData
        .from('jobs')
        .select(`
          *,
          customers (
            first_name,
            last_name,
            name,
            phone,
            address,
            city
          )
        `)
        .eq('company_id', companyId)
        .eq('scheduled_date', today)
        .order('scheduled_time', { ascending: true });

      if (error) {
        if (retryCount < 1) {
          console.warn('Today: query error, retrying...', error.message);
          await safeRefreshSession();
          await new Promise(r => setTimeout(r, 500));
          return loadTodaysJobs(retryCount + 1);
        }
        throw error;
      }

      // Split into categories
      const upcoming = jobs?.filter(j => j.status === 'scheduled') || [];
      const inProgress = jobs?.filter(j => j.status === 'in-progress') || [];
      const completed = jobs?.filter(j => j.status === 'completed') || [];

      setUpcomingJobs(upcoming);
      setInProgressJobs(inProgress);
      setCompletedJobs(completed);
    } catch (error) {
      // Fallback to cache
      if (isOfflineModeEnabled()) {
        const cachedJobs = getCachedData('jobs', companyId);
        if (cachedJobs) {
          const today = getTodayString();
          const todaysJobs = cachedJobs.filter(j => j.scheduled_date === today);
          setUpcomingJobs(todaysJobs.filter(j => j.status === 'scheduled'));
          setInProgressJobs(todaysJobs.filter(j => j.status === 'in-progress'));
          setCompletedJobs(todaysJobs.filter(j => j.status === 'completed'));
          setLoading(false);
          return;
        }
      }
      console.error('Today: failed to load jobs', error.message);
    } finally {
      setLoading(false);
    }
  };

  const getCustomerName = (customer) => {
    if (!customer) return 'Unknown Customer';
    if (customer.first_name && customer.last_name) {
      return `${customer.first_name} ${customer.last_name}`;
    }
    return customer.name || 'Unknown';
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const handleStartJob = async (jobId) => {
    try {
      const { error } = await supabaseData
        .from('jobs')
        .update({ status: 'in-progress' })
        .eq('id', jobId);

      if (error) throw error;
      captureLocation(user?.id, companyId, 'job_start');
      loadTodaysJobs();
    } catch (error) {
      showError('Error starting job: ' + error.message);
    }
  };

  const handleCompleteJob = async (job) => {
    // Check if this is a quote_visit job - use special modal
    if (job.job_type === 'quote_visit') {
      setSelectedQuoteVisitJob(job);
      setShowQuoteVisitModal(true);
      return;
    }
    // Open the completion modal instead of just marking complete
    setSelectedJobForCompletion(job);
    setShowCompletionModal(true);
  };

  const handleJobCompleted = (invoice) => {
    // Called after invoice is generated
    setShowCompletionModal(false);
    setSelectedJobForCompletion(null);
    loadTodaysJobs();
  };

  const handleSendMessage = async (jobId) => {
    const text = messageTexts[jobId] || '';
    if (!text.trim()) return;

    try {
      const { error } = await supabaseData
        .from('communications')
        .insert([{
          company_id: companyId,
          job_id: jobId,
          message: text,
          direction: 'outbound',
          type: 'internal_note',
          created_at: new Date().toISOString()
        }]);

      if (error) throw error;

      setMessageTexts(prev => ({ ...prev, [jobId]: '' }));
      showSuccess('Message sent!');
    } catch (error) {
      showError('Error sending message: ' + error.message);
    }
  };

  // Helper to get/set message text per job
  const getMessageText = (jobId) => messageTexts[jobId] || '';
  const updateMessageText = (jobId, value) => {
    setMessageTexts(prev => ({ ...prev, [jobId]: value }));
  };

  // Quick-send SMS with template
  const handleQuickSend = async (job, template) => {
    if (!job.customers?.phone) {
      showError('Customer has no phone number');
      return;
    }

    const sendKey = `${job.id}-${template.id}`;
    setSendingQuickMsg(prev => ({ ...prev, [sendKey]: true }));

    try {
      // Get customer's first name
      const customerName = job.customers?.name ||
        `${job.customers?.first_name || ''} ${job.customers?.last_name || ''}`.trim() ||
        'there';
      const firstName = customerName.split(' ')[0];

      // Replace template variables
      const message = replaceTemplateVariables(template.message, {
        customer_name: firstName,
        customer_full_name: customerName,
        company_name: companyName,
        address: job.customers?.address || '',
        job_number: job.job_number || '',
        appointment_time: job.scheduled_time || '',
        appointment_date: job.scheduled_date || ''
      });

      // Send the SMS
      const result = await sendSMS({
        phone: job.customers.phone,
        message: message,
        customerId: job.customer_id,
        companyId: companyId,
        jobId: job.id,
        sentBy: user?.id
      });

      if (result.success) {
        showSuccess(`"${template.label}" sent!`);
      } else {
        showError(result.error || 'Failed to send message');
      }
    } catch (error) {
      showError('Error sending message: ' + error.message);
    } finally {
      setSendingQuickMsg(prev => ({ ...prev, [sendKey]: false }));
    }
  };

  const handleUpdateJob = async (jobId, updates) => {
    try {
      setSaving(true);
      const { error } = await supabaseData
        .from('jobs')
        .update(updates)
        .eq('id', jobId);

      if (error) throw error;

      setEditingJob(null);
      loadTodaysJobs();
      showSuccess('Job updated!');
    } catch (error) {
      showError('Error updating job: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelJob = async (job) => {
    if (!window.confirm(`Cancel job ${job.job_number}? This will remove it from your schedule.`)) return;

    try {
      setSaving(true);
      const { error } = await supabaseData
        .from('jobs')
        .update({ status: 'cancelled', scheduled_date: null, scheduled_time: null })
        .eq('id', job.id);

      if (error) throw error;

      loadTodaysJobs();
      showSuccess('Job cancelled');
    } catch (error) {
      showError('Error cancelling job: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSendInvite = async () => {
    if (inviteMethod === 'sms' && !invitePhone.trim()) {
      showError('Please enter a phone number');
      return;
    }
    if (inviteMethod === 'email' && !inviteEmail.trim()) {
      showError('Please enter an email address');
      return;
    }

    setSendingInvite(true);
    try {
      const response = await fetch('/api/customer-signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'send',
          phone: invitePhone || undefined,
          email: inviteEmail || undefined,
          method: inviteMethod,
          companyId
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to send invite');

      setInviteSent(true);
      setInviteUrl(data.signupUrl);

      if (data.smsSent) {
        showSuccess('Invite sent via text!');
      } else if (data.emailSent) {
        showSuccess('Invite sent via email!');
      } else {
        showSuccess('Invite link created - copy it below.');
      }
    } catch (error) {
      showError('Error: ' + error.message);
    } finally {
      setSendingInvite(false);
    }
  };

  const getDayName = () => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[new Date().getDay()];
  };

  const getFormattedDate = () => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                    'July', 'August', 'September', 'October', 'November', 'December'];
    const date = new Date();
    return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  };

  const JobCard = ({ job, showActions = true }) => {
    const isExpanded = expandedJobId === job.id;

    return (
    <div style={{
      background: colors.bgCard,
      borderRadius: '12px',
      padding: isMobile ? '1rem' : '1.5rem',
      border: isExpanded ? `2px solid ${colors.accent}` : `1px solid ${colors.border}`,
      marginBottom: '1rem',
      transition: 'border-color 0.2s'
    }}>
      <div
        onClick={() => setExpandedJobId(isExpanded ? null : job.id)}
        style={{
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'start',
          marginBottom: isExpanded ? '1rem' : '0',
          minHeight: '48px'
        }}
      >
        <div style={{ flex: 1 }}>
          {job.scheduled_time && (
            <div style={{
              display: 'inline-block',
              padding: '0.35rem 0.85rem',
              background: colors.accent,
              color: '#000',
              borderRadius: '6px',
              fontSize: '0.9rem',
              fontWeight: 'bold',
              marginBottom: '0.5rem'
            }}>
              {formatTime(job.scheduled_time)}
            </div>
          )}
          <h3 style={{
            fontSize: isMobile ? '1.15rem' : '1.25rem',
            color: colors.text,
            margin: '0.5rem 0'
          }}>
            {getCustomerName(job.customers)}
          </h3>
          <div style={{
            color: colors.textMuted,
            fontSize: '0.9rem',
            marginBottom: '0.25rem'
          }}>
            {job.job_type || 'Service Call'}
            {job.estimated_duration && ` • ${job.estimated_duration >= 60 ? `${job.estimated_duration / 60}h` : `${job.estimated_duration}m`}`}
          </div>
          {/* Tap hint for collapsed cards */}
          {!isExpanded && (
            <div style={{
              color: colors.textMuted,
              fontSize: '0.8rem',
              marginTop: '0.25rem'
            }}>
              Tap for details
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{
            fontSize: isMobile ? '1.15rem' : '1.25rem',
            fontWeight: 'bold',
            color: colors.success
          }}>
            ${job.quoted_value?.toLocaleString() || '0'}
          </div>
          {isExpanded ? <ChevronUp size={24} color={colors.accent} /> : <ChevronDown size={24} color={colors.textMuted} />}
        </div>
      </div>

      {isExpanded && (
        <div style={{
          borderTop: `1px solid ${colors.border}`,
          paddingTop: '1rem',
          marginTop: '1rem'
        }}>
          {/* Address with navigation */}
          {job.customers?.address && (
            <a
              href={`https://maps.google.com/?q=${encodeURIComponent(
                `${job.customers.address}, ${job.customers.city || ''} ${job.customers.state || ''} ${job.customers.zip || ''}`
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '1rem',
                background: colors.bgInput,
                borderRadius: '8px',
                color: colors.success,
                textDecoration: 'none',
                marginBottom: '0.75rem',
                minHeight: '48px'
              }}
            >
              <MapPin size={20} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.9rem', fontWeight: '600' }}>
                  {job.customers.address}
                </div>
                <div style={{ fontSize: '0.8rem', color: colors.textMuted }}>
                  {[job.customers.city, job.customers.state, job.customers.zip].filter(Boolean).join(', ')}
                </div>
              </div>
              <Navigation size={18} />
            </a>
          )}

          {/* Phone */}
          {job.customers?.phone && (
            <a
              href={`tel:${job.customers.phone}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '1rem',
                background: colors.bgInput,
                borderRadius: '8px',
                color: colors.success,
                textDecoration: 'none',
                marginBottom: '0.75rem',
                minHeight: '48px'
              }}
            >
              <Phone size={20} />
              <span style={{ fontSize: '0.95rem', fontWeight: '600' }}>
                {job.customers.phone}
              </span>
            </a>
          )}

          {/* Notes */}
          {job.notes && (
            <div style={{
              padding: '1rem',
              background: colors.bgInput,
              borderRadius: '8px',
              marginBottom: '0.75rem',
              borderLeft: `3px solid ${colors.warning}`
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '0.5rem'
              }}>
                <MessageSquare size={16} color={colors.warning} />
                <span style={{
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  color: colors.textMuted,
                  textTransform: 'uppercase'
                }}>
                  Notes
                </span>
              </div>
              <p style={{
                margin: 0,
                fontSize: '0.9rem',
                lineHeight: 1.5,
                color: colors.text
              }}>
                {job.notes}
              </p>
            </div>
          )}

          {/* Job Number */}
          <div style={{
            padding: '0.75rem',
            background: colors.bgInput,
            borderRadius: '8px',
            marginBottom: '0.75rem'
          }}>
            <div style={{ fontSize: '0.75rem', color: colors.textMuted, marginBottom: '0.25rem' }}>
              Job Number
            </div>
            <div style={{
              fontSize: '0.9rem',
              fontFamily: 'monospace',
              color: colors.text,
              fontWeight: 'bold'
            }}>
              {job.job_number}
            </div>
          </div>

          {/* Messaging */}
          <div style={{
            padding: '1rem',
            background: colors.bgInput,
            borderRadius: '8px',
            border: `1px solid ${colors.border}`,
            opacity: textingEnabled ? 1 : 0.6
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '1rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {!textingEnabled && <Lock size={14} color={colors.textMuted} />}
                <MessageSquare size={18} color={textingEnabled ? colors.info : colors.textMuted} />
                <span style={{
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  color: textingEnabled ? colors.text : colors.textMuted
                }}>
                  Messages
                </span>
              </div>
              {!textingEnabled && (
                <button
                  onClick={() => onNavigate('settings', { section: 'subscription' })}
                  style={{
                    padding: '0.35rem 0.75rem',
                    background: colors.accent,
                    border: 'none',
                    borderRadius: '6px',
                    color: '#000',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  Enable
                </button>
              )}
            </div>

            {textingEnabled ? (
              <>
                {/* Quick-send buttons */}
                <div style={{
                  display: 'flex',
                  gap: '0.5rem',
                  marginBottom: '0.75rem',
                  flexWrap: 'wrap'
                }}>
                  {quickTemplates.map(template => {
                    const sendKey = `${job.id}-${template.id}`;
                    const isSending = sendingQuickMsg[sendKey];
                    return (
                      <button
                        key={template.id}
                        onClick={() => handleQuickSend(job, template)}
                        disabled={isSending || !job.customers?.phone}
                        style={{
                          padding: '0.5rem 0.75rem',
                          background: isSending ? colors.bgInput : colors.bgCard,
                          border: `1px solid ${colors.border}`,
                          borderRadius: '6px',
                          color: isSending ? colors.textMuted : colors.text,
                          fontSize: '0.8rem',
                          fontWeight: '500',
                          cursor: isSending || !job.customers?.phone ? 'not-allowed' : 'pointer',
                          opacity: !job.customers?.phone ? 0.5 : 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.35rem'
                        }}
                        title={!job.customers?.phone ? 'Customer has no phone number' : `Send "${template.label}" to customer`}
                      >
                        <Send size={12} />
                        {isSending ? 'Sending...' : template.label}
                      </button>
                    );
                  })}
                </div>

                {/* Message input */}
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="text"
                    value={getMessageText(job.id)}
                    onChange={(e) => updateMessageText(job.id, e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleSendMessage(job.id);
                      }
                    }}
                    placeholder="Send a message..."
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: colors.bgCard,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '0.9rem',
                      minHeight: '44px'
                    }}
                  />
                  <button
                    onClick={() => handleSendMessage(job.id)}
                    disabled={!getMessageText(job.id).trim()}
                    style={{
                      padding: '0.75rem',
                      background: getMessageText(job.id).trim() ? colors.info : colors.bgInput,
                      border: 'none',
                      borderRadius: '8px',
                      color: getMessageText(job.id).trim() ? '#fff' : colors.textMuted,
                      cursor: getMessageText(job.id).trim() ? 'pointer' : 'not-allowed',
                      minWidth: '44px',
                      minHeight: '44px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Send size={20} />
                  </button>
                </div>
              </>
            ) : (
              <div style={{
                padding: '1rem',
                background: colors.bgCard,
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <p style={{ margin: '0 0 0.5rem', color: colors.textMuted, fontSize: '0.85rem' }}>
                  SMS texting is not enabled on your plan
                </p>
                <p style={{ margin: 0, color: colors.textMuted, fontSize: '0.8rem' }}>
                  $20/mo for 500 messages
                </p>
              </div>
            )}
          </div>

          {/* Job Photos */}
          <div style={{
            marginTop: '0.75rem',
            padding: '1rem',
            background: colors.bgInput,
            borderRadius: '8px',
            border: `1px solid ${colors.border}`
          }}>
            <JobPhotos
              jobId={job.id}
              companyId={companyId}
            />
          </div>
        </div>
      )}

      {showActions && (
        <div style={{ marginTop: '1rem' }}>
          {job.status === 'scheduled' && (
            <button
              onClick={(e) => { e.stopPropagation(); handleStartJob(job.id); }}
              style={{
                width: '100%',
                padding: isMobile ? '1.25rem' : '1rem',
                background: `linear-gradient(135deg, ${colors.success} 0%, #45b393 100%)`,
                color: '#000',
                border: 'none',
                borderRadius: '10px',
                fontSize: isMobile ? '1.15rem' : '1rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.75rem',
                minHeight: '56px',
                boxShadow: '0 4px 12px rgba(78, 204, 163, 0.3)'
              }}
            >
              <Play size={isMobile ? 26 : 22} fill="#000" />
              {job.job_type === 'quote_visit' ? 'Begin Visit' : 'Start Job'}
            </button>
          )}
          {job.status === 'in-progress' && job.job_type === 'quote_visit' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <button
                onClick={(e) => { e.stopPropagation(); onNavigate('quote-builder', { customerId: job.customer_id, jobId: job.id }); }}
                style={{
                  width: '100%',
                  padding: isMobile ? '1rem' : '0.875rem',
                  background: colors.accentGradient,
                  color: '#fff',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: isMobile ? '1rem' : '0.95rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.75rem',
                  minHeight: '48px'
                }}
              >
                <FileText size={isMobile ? 22 : 18} />
                Build Quote
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onNavigate('job-planner', { jobId: job.id }); }}
                style={{
                  width: '100%',
                  padding: isMobile ? '1rem' : '0.875rem',
                  background: '#3498db',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: isMobile ? '1rem' : '0.95rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.75rem',
                  minHeight: '48px'
                }}
              >
                <Package size={isMobile ? 22 : 18} />
                Plan Job
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); handleCompleteJob(job); }}
                style={{
                  width: '100%',
                  padding: isMobile ? '1rem' : '0.875rem',
                  background: `linear-gradient(135deg, ${colors.success} 0%, #45b393 100%)`,
                  color: '#000',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: isMobile ? '1rem' : '0.95rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.75rem',
                  minHeight: '48px'
                }}
              >
                <CheckCircle size={isMobile ? 22 : 18} />
                Complete Visit
              </button>
            </div>
          )}
          {job.status === 'in-progress' && job.job_type !== 'quote_visit' && (
            <button
              onClick={(e) => { e.stopPropagation(); handleCompleteJob(job); }}
              style={{
                width: '100%',
                padding: isMobile ? '1.25rem' : '1rem',
                background: `linear-gradient(135deg, ${colors.success} 0%, #45b393 100%)`,
                color: '#000',
                border: 'none',
                borderRadius: '10px',
                fontSize: isMobile ? '1.15rem' : '1rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.75rem',
                minHeight: '56px',
                boxShadow: '0 4px 12px rgba(78, 204, 163, 0.3)'
              }}
            >
              <CheckCircle size={isMobile ? 26 : 22} />
              Complete Job
            </button>
          )}

          {/* Edit and Cancel buttons */}
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.75rem' }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setEditingJob({
                  id: job.id,
                  status: job.status,
                  job_type: job.job_type,
                  quoted_value: job.quoted_value,
                  notes: job.notes
                });
              }}
              style={{
                flex: 1,
                padding: '0.75rem',
                background: colors.bgInput,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: colors.accent,
                fontSize: '0.9rem',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                minHeight: '44px'
              }}
            >
              <Edit2 size={18} />
              Edit
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); handleCancelJob(job); }}
              disabled={saving}
              style={{
                flex: 1,
                padding: '0.75rem',
                background: colors.bgInput,
                border: `1px solid ${colors.error || '#dc2626'}`,
                borderRadius: '8px',
                color: colors.error || '#dc2626',
                fontSize: '0.9rem',
                fontWeight: '600',
                cursor: saving ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                minHeight: '44px',
                opacity: saving ? 0.6 : 1
              }}
            >
              <Trash2 size={18} />
              Cancel Job
            </button>
          </div>
        </div>
      )}
    </div>
  )};

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: colors.textMuted }}>
        Loading today's schedule...
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '0 1rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {companyLogo && (
            <img
              src={companyLogo}
              alt="Logo"
              style={{
                width: '48px',
                height: '48px',
                objectFit: 'contain',
                borderRadius: '8px'
              }}
            />
          )}
          <h1 style={{
            fontSize: '2.5rem',
            margin: 0,
            color: colors.text
          }}>
            Today
          </h1>
        </div>
        <div style={{
          fontSize: '1.1rem',
          color: colors.textMuted
        }}>
          {getDayName()}, {getFormattedDate()}
        </div>
      </div>

      {/* Chrome-style Tabs */}
      <div style={{
        display: 'flex',
        gap: '2px',
        marginBottom: '1.5rem',
        background: colors.bgInput,
        borderRadius: '12px',
        padding: '3px',
      }}>
        {[
          { id: 'today', label: 'Today', icon: <Clock size={16} /> },
          { id: 'inventory', label: 'Inventory', icon: <Package size={16} />, badge: lowStockItems.length > 0 ? lowStockItems.length : null },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              flex: 1,
              padding: '0.6rem 1rem',
              background: activeTab === tab.id ? colors.bgCard : 'transparent',
              border: 'none',
              borderRadius: '10px',
              color: activeTab === tab.id ? colors.text : colors.textMuted,
              fontSize: '0.9rem',
              fontWeight: activeTab === tab.id ? '700' : '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              transition: 'all 0.15s',
              boxShadow: activeTab === tab.id ? colors.shadow : 'none',
              position: 'relative'
            }}
          >
            {tab.icon}
            {tab.label}
            {tab.badge && (
              <span style={{
                background: colors.warning,
                color: '#fff',
                fontSize: '0.7rem',
                fontWeight: '700',
                borderRadius: '10px',
                padding: '0.1rem 0.4rem',
                minWidth: '18px',
                textAlign: 'center',
                lineHeight: '1.2'
              }}>
                {tab.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Inventory Tab */}
      {activeTab === 'inventory' && (
        <div>
          {/* Low Stock Alert Banner */}
          {lowStockItems.length > 0 && (
            <div
              style={{
                background: `${colors.warning}15`,
                border: `1px solid ${colors.warning}40`,
                borderRadius: '12px',
                padding: '1rem 1.25rem',
                marginBottom: '1.5rem',
                cursor: 'pointer'
              }}
              onClick={() => onNavigate('inventory')}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                <Package size={20} color={colors.warning} />
                <span style={{ color: colors.warning, fontWeight: 'bold' }}>
                  {lowStockItems.length} item{lowStockItems.length !== 1 ? 's' : ''} low on stock
                </span>
              </div>
              <div style={{ fontSize: '0.85rem', color: colors.textMuted, paddingLeft: '2rem' }}>
                {lowStockItems.slice(0, 3).map(item => (
                  <div key={item.id}>{item.name}: {item.quantity_on_hand} left (reorder at {item.reorder_point})</div>
                ))}
                {lowStockItems.length > 3 && (
                  <div style={{ color: colors.accent, marginTop: '0.25rem' }}>
                    + {lowStockItems.length - 3} more — tap to view inventory
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Inventory Health Widget */}
          <InventoryHealthWidget companyId={companyId} onNavigate={onNavigate} />
        </div>
      )}

      {/* Today Tab Content */}
      {activeTab === 'today' && <>

      {/* Card Payment Reminder Banner - Shows if no Stripe payments in 1+ week */}
      {paymentBanner === 'card_reminder' && (
        <div style={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          borderRadius: '12px',
          padding: '1rem 1.25rem',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '1rem',
          position: 'relative',
          boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
          cursor: 'pointer'
        }}
        onClick={() => {
          dismissPaymentBanner('card_reminder');
          onNavigate('settings', { section: 'payment' });
        }}
        >
          <div style={{
            width: '44px',
            height: '44px',
            borderRadius: '10px',
            background: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <DollarSign size={24} color="#fff" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ color: '#fff', fontWeight: 'bold', fontSize: '1.1rem', marginBottom: '0.25rem' }}>
              Accept Card Payments In-App
            </div>
            <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem', lineHeight: 1.4 }}>
              Get paid faster! Accept credit cards, debit cards, and digital wallets directly through Work Van.
              Funds deposit to your bank automatically.
            </div>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              dismissPaymentBanner('card_reminder');
            }}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '6px',
              padding: '0.35rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <X size={18} color="#fff" />
          </button>
        </div>
      )}

      {/* Financing Reminder Banner - Shows every 2 weeks until they've done BNPL */}
      {paymentBanner === 'financing_reminder' && (
        <div style={{
          background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 50%, #4f46e5 100%)',
          borderRadius: '12px',
          padding: '1rem 1.25rem',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '1rem',
          position: 'relative',
          boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
          cursor: 'pointer'
        }}
        onClick={() => {
          dismissPaymentBanner('financing_reminder');
          onNavigate('settings', { section: 'payment' });
        }}
        >
          <div style={{
            width: '44px',
            height: '44px',
            borderRadius: '10px',
            background: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <Sparkles size={24} color="#fff" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ color: '#fff', fontWeight: 'bold', fontSize: '1.1rem', marginBottom: '0.25rem' }}>
              Offer Financing - Free for You!
            </div>
            <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem', lineHeight: 1.4 }}>
              Let customers pay with <strong>Afterpay</strong>, <strong>Klarna</strong>, or <strong>Affirm</strong>.
              They pay in installments, you get paid in full immediately. Close more big-ticket deals!
            </div>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              dismissPaymentBanner('financing_reminder');
            }}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '6px',
              padding: '0.35rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <X size={18} color="#fff" />
          </button>
        </div>
      )}

      {/* Quick Actions - Mobile Optimized */}
      <div style={{
        background: colors.bgCard,
        borderRadius: '12px',
        padding: isMobile ? '1rem' : '1.5rem',
        marginBottom: '2rem',
        border: `1px solid ${colors.border}`
      }}>
        {/* Primary Action - New Job (paid work) */}
        <button
          onClick={() => onNavigate('express-job', { workflowType: 'full' })}
          style={{
            width: '100%',
            padding: isMobile ? '1.5rem' : '1.25rem',
            background: colors.accentGradient,
            border: 'none',
            borderRadius: '12px',
            color: '#000',
            fontSize: isMobile ? '1.25rem' : '1.1rem',
            fontWeight: 'bold',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem',
            boxShadow: '0 4px 12px rgba(240, 90, 40, 0.3)',
            minHeight: '56px'
          }}
        >
          <Zap size={isMobile ? 28 : 24} />
          New Job
        </button>

        {/* Secondary Action - New Visit (quote, consult, meeting — non-paid) */}
        <button
          onClick={() => onNavigate('express-job', { workflowType: 'quick' })}
          style={{
            width: '100%',
            marginTop: '0.75rem',
            padding: isMobile ? '1rem' : '0.875rem',
            background: 'transparent',
            border: `2px solid ${colors.info}`,
            borderRadius: '12px',
            color: colors.info,
            fontSize: isMobile ? '1rem' : '0.95rem',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            minHeight: '48px'
          }}
        >
          <Calendar size={isMobile ? 22 : 18} />
          New Visit
          <span style={{
            fontSize: '0.75rem',
            opacity: 0.75,
            fontWeight: '500',
          }}>
            (quote, consult, meeting)
          </span>
        </button>

        {/* New Customer Info */}
        <button
          onClick={() => {
            setInvitePhone('');
            setInviteEmail('');
            setInviteMethod('email');
            setInviteSent(false);
            setInviteUrl('');
            setShowInviteModal(true);
          }}
          style={{
            width: '100%',
            marginTop: '0.75rem',
            padding: isMobile ? '1rem' : '0.875rem',
            background: colors.bgInput,
            border: `1px solid ${colors.border}`,
            borderRadius: '10px',
            color: colors.text,
            fontSize: isMobile ? '1rem' : '0.95rem',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.6rem',
            minHeight: '48px'
          }}
        >
          <UserPlus size={isMobile ? 22 : 20} />
          New Customer Info
        </button>

        {/* Accept Payments CTA - show when no payment processor connected */}
        {!companyData?.stripe_connect_id && !companyData?.square_merchant_id && (
          <button
            onClick={() => onNavigate('settings', { section: 'payment' })}
            style={{
              width: '100%',
              marginTop: '0.75rem',
              padding: isMobile ? '0.75rem' : '0.625rem',
              background: '#ffffff',
              border: '1.5px solid #e0e0e0',
              borderRadius: '10px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '54px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              transition: 'box-shadow 0.2s, border-color 0.2s'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(99,91,255,0.15)';
              e.currentTarget.style.borderColor = '#635bff';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
              e.currentTarget.style.borderColor = '#e0e0e0';
            }}
          >
            <span style={{
              fontSize: isMobile ? '0.95rem' : '0.9rem',
              fontWeight: '600',
              color: '#1a1a1a',
              textAlign: 'center',
              lineHeight: '1.3',
            }}>
              Sign up to accept card payments &amp; financing
            </span>
          </button>
        )}
      </div>

      {/* Customer Invite Modal */}
      {showInviteModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: colors.bgCard,
            borderRadius: '12px',
            maxWidth: '400px',
            width: '100%',
            border: `1px solid ${colors.border}`
          }}>
            {/* Header */}
            <div style={{
              padding: '1.5rem',
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ margin: 0, color: colors.text, fontSize: '1.25rem' }}>
                New Customer Info
              </h2>
              <button
                onClick={() => setShowInviteModal(false)}
                style={{
                  background: 'none', border: 'none',
                  color: colors.textMuted, cursor: 'pointer', padding: '0.5rem'
                }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div style={{ padding: '1.5rem' }}>
              {!inviteSent ? (
                <>
                  <p style={{ color: colors.textMuted, marginBottom: '1rem', fontSize: '0.9rem' }}>
                    Send the customer a link to fill out their own info.
                  </p>

                  {/* Method Toggle */}
                  <div style={{ display: 'flex', marginBottom: '1rem', borderRadius: '8px', overflow: 'hidden', border: `1px solid ${colors.border}` }}>
                    <button
                      onClick={() => setInviteMethod('sms')}
                      style={{
                        flex: 1, padding: '0.6rem',
                        background: inviteMethod === 'sms' ? colors.accent : colors.bgInput,
                        color: inviteMethod === 'sms' ? '#fff' : colors.textMuted,
                        border: 'none', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.85rem',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem'
                      }}
                    >
                      <Phone size={15} /> Text
                    </button>
                    <button
                      onClick={() => setInviteMethod('email')}
                      style={{
                        flex: 1, padding: '0.6rem',
                        background: inviteMethod === 'email' ? colors.accent : colors.bgInput,
                        color: inviteMethod === 'email' ? '#fff' : colors.textMuted,
                        border: 'none', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.85rem',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem'
                      }}
                    >
                      <Mail size={15} /> Email
                    </button>
                  </div>

                  {inviteMethod === 'sms' ? (
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.textMuted, fontSize: '0.9rem' }}>
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={invitePhone}
                        onChange={(e) => setInvitePhone(e.target.value)}
                        placeholder="(555) 123-4567"
                        style={{
                          width: '100%', padding: '0.75rem 1rem',
                          background: colors.bgInput, border: `1px solid ${colors.border}`,
                          borderRadius: '8px', color: colors.text, fontSize: '1rem'
                        }}
                      />
                    </div>
                  ) : (
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.textMuted, fontSize: '0.9rem' }}>
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        placeholder="customer@example.com"
                        style={{
                          width: '100%', padding: '0.75rem 1rem',
                          background: colors.bgInput, border: `1px solid ${colors.border}`,
                          borderRadius: '8px', color: colors.text, fontSize: '1rem'
                        }}
                      />
                    </div>
                  )}

                  <button
                    onClick={handleSendInvite}
                    disabled={sendingInvite}
                    style={{
                      width: '100%', padding: '0.875rem',
                      background: colors.accent, color: '#fff',
                      border: 'none', borderRadius: '8px',
                      fontSize: '1rem', fontWeight: 'bold',
                      cursor: sendingInvite ? 'wait' : 'pointer',
                      opacity: sendingInvite ? 0.7 : 1,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem'
                    }}
                  >
                    {sendingInvite ? (
                      <>
                        <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send size={18} />
                        Send Invite
                      </>
                    )}
                  </button>
                  <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
                </>
              ) : (
                <>
                  <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                    <CheckCircle size={48} color="#10b981" />
                    <h3 style={{ color: colors.text, marginTop: '1rem' }}>Invite Sent!</h3>
                    <p style={{ color: colors.textMuted, fontSize: '0.9rem' }}>
                      The customer will receive {inviteMethod === 'email' ? 'an email' : 'a text'} with a link to fill out their information.
                    </p>
                  </div>
                  {inviteUrl && (
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.textMuted, fontSize: '0.85rem' }}>
                        Link (copy if needed):
                      </label>
                      <input
                        type="text"
                        value={inviteUrl}
                        readOnly
                        onClick={(e) => e.target.select()}
                        style={{
                          width: '100%', padding: '0.5rem 0.75rem',
                          background: colors.bgInput, border: `1px solid ${colors.border}`,
                          borderRadius: '6px', color: colors.textMuted, fontSize: '0.8rem'
                        }}
                      />
                    </div>
                  )}
                  <button
                    onClick={() => setShowInviteModal(false)}
                    style={{
                      width: '100%', padding: '0.875rem',
                      background: colors.bgInput, border: `1px solid ${colors.border}`,
                      borderRadius: '8px', color: colors.text,
                      fontSize: '1rem', cursor: 'pointer'
                    }}
                  >
                    Done
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Upcoming Jobs */}
      {upcomingJobs.length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <h2 style={{
            fontSize: '1.25rem',
            color: colors.text,
            margin: '0 0 1rem 0',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <Clock size={24} color={colors.info} />
            Upcoming Today ({upcomingJobs.length})
          </h2>
          {upcomingJobs.map(job => (
            <JobCard key={job.id} job={job} />
          ))}
        </div>
      )}

      {/* In Progress */}
      {inProgressJobs.length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <h2 style={{
            fontSize: '1.25rem',
            color: colors.text,
            margin: '0 0 1rem 0',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <Play size={24} color={colors.warning} />
            In Progress ({inProgressJobs.length})
          </h2>
          {inProgressJobs.map(job => (
            <JobCard key={job.id} job={job} />
          ))}
        </div>
      )}

      {/* Completed Today */}
      {completedJobs.length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <h2 style={{
            fontSize: '1.25rem',
            color: colors.text,
            margin: '0 0 1rem 0',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <CheckCircle size={24} color={colors.success} />
            Completed Today ({completedJobs.length})
          </h2>
          {completedJobs.map(job => (
            <JobCard key={job.id} job={job} showActions={false} />
          ))}
        </div>
      )}

      {/* Empty State - Clear and Actionable */}
      {upcomingJobs.length === 0 && inProgressJobs.length === 0 && completedJobs.length === 0 && (
        <div style={{
          background: colors.bgCard,
          borderRadius: '12px',
          padding: isMobile ? '2.5rem 1.5rem' : '4rem 2rem',
          textAlign: 'center',
          border: `2px dashed ${colors.border}`
        }}>
          {companyLogo ? (
            <img
              src={companyLogo}
              alt="Company Logo"
              style={{
                width: isMobile ? '80px' : '120px',
                height: isMobile ? '80px' : '120px',
                objectFit: 'contain',
                marginBottom: '1rem',
                borderRadius: '12px'
              }}
            />
          ) : (
            <div style={{
              width: isMobile ? '80px' : '120px',
              height: isMobile ? '80px' : '120px',
              background: colors.accentGradient,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem',
              fontSize: isMobile ? '2rem' : '3rem'
            }}>
              <Zap color="#000" size={isMobile ? 36 : 48} />
            </div>
          )}
          <h3 style={{
            color: colors.text,
            marginBottom: '0.75rem',
            fontSize: isMobile ? '1.25rem' : '1.5rem'
          }}>
            No jobs scheduled today
          </h3>
          <p style={{
            color: colors.textMuted,
            marginBottom: '1.5rem',
            fontSize: '1rem'
          }}>
            Ready to start working?
          </p>
          <button
            onClick={() => onNavigate('express-job')}
            style={{
              padding: '1rem 2rem',
              background: colors.accentGradient,
              border: 'none',
              borderRadius: '10px',
              color: '#000',
              fontSize: '1.1rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              minHeight: '50px'
            }}
          >
            <Zap size={22} />
            Create a Job
          </button>
        </div>
      )}

    {/* Job Completion Modal */}
    {showCompletionModal && selectedJobForCompletion && (
      <JobCompletionModal
        job={selectedJobForCompletion}
        companyId={companyId}
        onClose={() => {
          setShowCompletionModal(false);
          setSelectedJobForCompletion(null);
        }}
        onComplete={handleJobCompleted}
        onNavigate={onNavigate}
      />
    )}

    {/* Quote Visit Completion Modal */}
    {showQuoteVisitModal && selectedQuoteVisitJob && (
      <QuoteVisitCompletionModal
        job={selectedQuoteVisitJob}
        companyId={companyId}
        onClose={() => {
          setShowQuoteVisitModal(false);
          setSelectedQuoteVisitJob(null);
        }}
        onComplete={() => {
          setShowQuoteVisitModal(false);
          setSelectedQuoteVisitJob(null);
          loadTodaysJobs();
        }}
        onNavigate={onNavigate}
      />
    )}

    {/* Edit Job Modal */}
    {editingJob && (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}
        onClick={() => setEditingJob(null)}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: colors.bgCard,
            borderRadius: '12px',
            width: '100%',
            maxWidth: '450px',
            padding: '1.5rem',
            border: `1px solid ${colors.border}`
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h3 style={{ margin: 0, color: colors.text, fontSize: '1.25rem' }}>Edit Job</h3>
            <button
              onClick={() => setEditingJob(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
            >
              <X size={24} color={colors.textMuted} />
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>Status</label>
              <select
                value={editingJob.status || ''}
                onChange={(e) => setEditingJob({ ...editingJob, status: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: colors.bgInput,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  color: colors.text,
                  fontSize: '1rem'
                }}
              >
                <option value="scheduled">Scheduled</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>Job Type</label>
              <input
                type="text"
                value={editingJob.job_type || ''}
                onChange={(e) => setEditingJob({ ...editingJob, job_type: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: colors.bgInput,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  color: colors.text,
                  fontSize: '1rem'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>Quoted Value</label>
              <input
                type="number"
                value={editingJob.quoted_value || ''}
                onChange={(e) => setEditingJob({ ...editingJob, quoted_value: parseFloat(e.target.value) || 0 })}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: colors.bgInput,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  color: colors.text,
                  fontSize: '1rem'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>Notes</label>
              <textarea
                value={editingJob.notes || ''}
                onChange={(e) => setEditingJob({ ...editingJob, notes: e.target.value })}
                rows={3}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: colors.bgInput,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  color: colors.text,
                  fontSize: '1rem',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
              <button
                onClick={() => setEditingJob(null)}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: colors.bgInput,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  color: colors.textMuted,
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleUpdateJob(editingJob.id, {
                  status: editingJob.status,
                  job_type: editingJob.job_type,
                  quoted_value: editingJob.quoted_value,
                  notes: editingJob.notes
                })}
                disabled={saving}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: colors.success,
                  border: 'none',
                  borderRadius: '8px',
                  color: '#000',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontWeight: '600'
                }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
    </>}
    </div>
  );
}
